// Sea routes (taxgw-nhpf, ROADMAP D39, docs/ART-DIRECTION.md §1): every voyage the player's warships and transport
// boats have sailed stays on the map for the rest of the war, a still dashed line in the player's colour just under the
// moving trip lines (TransportRouteLayer). The sim sends a route once (SimEvent seaRoute, the seat's own only) when the
// seat's first vessel sets out on it, and an empty one when its bounded memory forgets a route; a view resync re-sends
// them all. The layer keeps the routes by port pair and rebuilds its one line geometry only when that set changes (a
// few times a war, never per frame), each leg tessellated along its great circle (ROUTE_STEP_RAD, the shared policy).

import { AdditiveBlending, BufferAttribute, BufferGeometry, LineSegments, ShaderMaterial, Vector3 } from 'three'
import { InvariantViolation, MAX_SEA_ROUTE_POINTS, type PlayerId, type SimEvent } from '@taxwar/net'
import { GLOBE_ALTITUDE, latLonToVector3 } from '../geo/globeSpace'
import { ROUTE_STEP_RAD } from '../geo/routeTessellation'
import { EMISSIVE_LAYER, assignLayer } from '../layers'
import { factionRole, type Palette } from '../palette'
import { ROUTE_FRAGMENT, ROUTE_VERTEX } from './routeShaders'

/** Just above the war network and just under the moving trip lines (ART-DIRECTION §2.4: never under geography). */
const SEA_ROUTE_ALTITUDE = GLOBE_ALTITUDE.network + 0.001
/** Dimmer than a moving trip: a sailed route is memory, a trip is what happens now (ART-DIRECTION §2.5). */
const SEA_ROUTE_INTENSITY = 0.45
/** Dash period along a route in radians of great-circle arc: longer than a trip's, so the two read apart. */
const SEA_ROUTE_DASH_PERIOD = 0.03
const SEA_ROUTE_DASH_DUTY = 0.5
/** The dashes stand still: only a trip's flow toward its destination moves. */
const STILL_DASH_PHASE = 0
const MAX_LATITUDE_DEG = 90
/** Below this angle a leg has no length (a repeated point); the same bound on its sine marks antipodal ends. */
const MIN_ARC_RAD = 1e-9
const LAT_LON_COMPONENTS = 2
const POSITION_COMPONENTS = 3
const COLOUR_COMPONENTS = 3

export class SeaRouteLayer {
  readonly root: LineSegments

  private readonly material: ShaderMaterial
  /** Port pair (`a-b`, a < b) → the route as flat [lat, lon, …] degrees from a to b; insertion order is draw order. */
  private readonly routes = new Map<string, readonly number[]>()
  private palette: Palette

  private readonly from = new Vector3()
  private readonly to = new Vector3()
  private readonly across = new Vector3()
  private readonly vertex = new Vector3()

  constructor(
    private readonly viewer: PlayerId,
    palette: Palette,
  ) {
    this.palette = palette
    this.material = new ShaderMaterial({
      uniforms: {
        uIntensity: { value: SEA_ROUTE_INTENSITY },
        uDashPeriod: { value: SEA_ROUTE_DASH_PERIOD },
        uDashPhase: { value: STILL_DASH_PHASE },
        uDashDuty: { value: SEA_ROUTE_DASH_DUTY },
      },
      vertexShader: ROUTE_VERTEX,
      fragmentShader: ROUTE_FRAGMENT,
      transparent: true,
      blending: AdditiveBlending,
      depthWrite: false,
    })
    this.root = new LineSegments(new BufferGeometry(), this.material)
    // The geometry changes as routes arrive; a cached bounding sphere would cull it wrongly.
    this.root.frustumCulled = false
    assignLayer(this.root, EMISSIVE_LAYER)
  }

  /** Match.onEvents listener: keeps every seaRoute (points add or replace the pair's route; empty forgets it). */
  readonly applyEvents = (events: readonly SimEvent[]): void => {
    let changed = false
    for (let i = 0; i < events.length; i++) {
      const event = events[i] as SimEvent
      if (event.kind !== 'seaRoute') continue
      this.setRoute(`${event.a}-${event.b}`, event.points)
      changed = true
    }
    if (changed) this.rebuild()
  }

  /** The routes take the new palette's own-side colour at once. */
  setPalette(palette: Palette): void {
    this.palette = palette
    this.rebuild()
  }

  dispose(): void {
    this.root.removeFromParent()
    this.root.geometry.dispose()
    this.material.dispose()
  }

  /** Validates `points` (the room's event is a boundary) and keeps them as the pair's route; empty forgets it. */
  private setRoute(pair: string, points: readonly number[]): void {
    if (points.length % LAT_LON_COMPONENTS !== 0) throw new InvariantViolation(`sea route ${pair} has an odd coordinate count ${points.length}`)
    const count = points.length / LAT_LON_COMPONENTS
    if (count === 0) {
      this.routes.delete(pair)
      return
    }
    if (count < 2 || count > MAX_SEA_ROUTE_POINTS) throw new InvariantViolation(`sea route ${pair} has ${count} points; a route has 2 to ${MAX_SEA_ROUTE_POINTS}`)
    for (let k = 0; k < points.length; k += LAT_LON_COMPONENTS) {
      const lat = points[k] as number
      const lon = points[k + 1] as number
      if (!Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > MAX_LATITUDE_DEG) {
        throw new InvariantViolation(`sea route ${pair} point ${k / LAT_LON_COMPONENTS} (${lat}, ${lon}) is not on the globe`)
      }
    }
    this.routes.set(pair, points)
  }

  /** Rebuilds the one line geometry from every kept route: each leg a great-circle arc, the dash distance continuous. */
  private rebuild(): void {
    const positions: number[] = []
    const distances: number[] = []
    for (const points of this.routes.values()) {
      let along = 0
      for (let k = LAT_LON_COMPONENTS; k < points.length; k += LAT_LON_COMPONENTS) {
        latLonToVector3(points[k - LAT_LON_COMPONENTS] as number, points[k - 1] as number, 1, this.from)
        latLonToVector3(points[k] as number, points[k + 1] as number, 1, this.to)
        along = this.pushArc(along, positions, distances)
      }
    }
    const colour = this.palette[factionRole(this.viewer, this.viewer)]
    const vertexCount = distances.length
    const colours = new Float32Array(vertexCount * COLOUR_COMPONENTS)
    for (let v = 0; v < vertexCount; v++) {
      colours[v * COLOUR_COMPONENTS] = colour.r
      colours[v * COLOUR_COMPONENTS + 1] = colour.g
      colours[v * COLOUR_COMPONENTS + 2] = colour.b
    }
    const geometry = new BufferGeometry()
    geometry.setAttribute('position', new BufferAttribute(new Float32Array(positions), POSITION_COMPONENTS))
    geometry.setAttribute('aColor', new BufferAttribute(colours, COLOUR_COMPONENTS))
    geometry.setAttribute('aDistance', new BufferAttribute(new Float32Array(distances), 1))
    this.root.geometry.dispose()
    this.root.geometry = geometry
  }

  /**
   * Pushes the great-circle arc from `this.from` to `this.to` (unit vectors) as segments of at most ROUTE_STEP_RAD,
   * the dash distance running on from `along`; returns the distance at the arc's end. A leg without length adds nothing.
   */
  private pushArc(along: number, positions: number[], distances: number[]): number {
    const angle = Math.atan2(this.across.crossVectors(this.from, this.to).length(), this.from.dot(this.to))
    if (angle < MIN_ARC_RAD) return along
    const sinAngle = Math.sin(angle)
    if (sinAngle < MIN_ARC_RAD) throw new InvariantViolation('sea route leg joins antipodal points; no single great circle spans it')
    const steps = Math.ceil(angle / ROUTE_STEP_RAD)
    for (let s = 0; s < steps; s++) {
      this.pushVertex(s / steps, angle, sinAngle, along, positions, distances)
      this.pushVertex((s + 1) / steps, angle, sinAngle, along, positions, distances)
    }
    return along + angle
  }

  /** One segment end: the point at fraction `f` of the arc, lifted to the route altitude, and its dash distance. */
  private pushVertex(f: number, angle: number, sinAngle: number, along: number, positions: number[], distances: number[]): void {
    const weightFrom = (Math.sin((1 - f) * angle) / sinAngle) * SEA_ROUTE_ALTITUDE
    const weightTo = (Math.sin(f * angle) / sinAngle) * SEA_ROUTE_ALTITUDE
    this.vertex.copy(this.from).multiplyScalar(weightFrom).addScaledVector(this.to, weightTo)
    positions.push(this.vertex.x, this.vertex.y, this.vertex.z)
    distances.push(along + f * angle)
  }
}
